/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from 'react';
import {
  Accordion,
  AccordionItem,
  AccordionItemButton,
  AccordionItemHeading,
  AccordionItemPanel,
} from 'react-accessible-accordion';
import 'react-accessible-accordion/dist/fancy-example.css';
import ArrowIcon from '../../../assets/arrowIcon.png';
import ArrowUpIcon from '../../../assets/arrowUpIcon.png';
import { RangeCriteriaDetail } from 'components/atoms/RangeCriteriaDetail/RangeCriteriaDetail';
import { MultiSelectCriteriaDetail } from 'components/atoms/MultiSelectCriteriaDetail/MultiSelectCriteriaDetail';
import { GeolocationCriteriaDetail } from 'components/atoms/GeolocationCriteriaDetail/GeolocationCriteriaDetail';
import { CriteriaFileDetail } from 'criterias/presentation/components/organisms/CriteriaFileDetail';
import { SwitchCriteriaDetail } from 'components/atoms/SwitchCriteriaDetail/SwitchCriteriaDetail';
import { MeasurementCriteriaDetail } from 'components/atoms/MeasurementCriteriaDetail/MeasurementCriteriaDetail';
import { CountryCriteriaDetail } from 'components/atoms/CountryCriteriaDetail/CountryCriteriaDetail';
import { ZonesIcon } from 'components/atoms/Icons/zonas';

interface Props {
    rules: {
        id: number
        chutes: number[]
        name: string
        criterias: any[]
    }[]
    isClassification?: boolean
}

const RuleKeys = {
    weight: 'weight',
    declared_value: 'declared_value',
    postal_code_range: 'postal_code_range',
    distribution_provider: 'distribution_provider',
    service_code: 'service_code',
    service_types: 'service_types',
    clients: 'clients',
    states: 'states',
    town: 'town',
    township: 'township',
    postal_code_list: 'postal_code_list',
    hs_code: 'hs_code',
    ddu_ddp: 'ddu_ddp',
    measurements: 'measurements',
    country: 'country',
    zone: 'zone',
}
const SORT_PRIORITY = {
    [RuleKeys.postal_code_range]: 1,
    [RuleKeys.states]: 2,
    [RuleKeys.town]: 3,
    [RuleKeys.township]: 4,
}
export const AccordionRules = ({ rules, isClassification = false }: Props) => {
    const [expandedItems, setExpandedItems] = useState<string[]>([])
    const sortCriteria = (criterias: any[]) => {
        return [...criterias].sort((a, b) => {
            const priorityA = SORT_PRIORITY[a.key] || 0
            const priorityB = SORT_PRIORITY[b.key] || 0
            return priorityA - priorityB
        })
    }

    const renderCriteria = (rule: any, groupId: number) => {
        switch (rule.key) {
            case RuleKeys.weight:
            case RuleKeys.declared_value:
            case RuleKeys.postal_code_range:
                return (
                    <RangeCriteriaDetail
                        id={rule.id}
                        name={rule.name}
                        valueKey={rule.key}
                        criteria_configurations={rule.criteria_configurations}
                    />
                )
            case RuleKeys.distribution_provider:
            case RuleKeys.service_code:
            case RuleKeys.service_types:
            case RuleKeys.clients:
                return (
                    <MultiSelectCriteriaDetail
                        id={rule.id}
                        name={rule.name}
                        valueKey={rule.key}
                        criteria_list={rule.criterial_list}
                    />
                )
            case RuleKeys.township:
            case RuleKeys.town:
            case RuleKeys.states:
                return (
                    <GeolocationCriteriaDetail
                        id={rule.id}
                        name={rule.name}
                        valueKey={rule.key}
                        criteria_list={rule.criterial_list}
                    />
                )
            case RuleKeys.postal_code_list:
            case RuleKeys.hs_code:
                return (
                    <CriteriaFileDetail
                        groupId={groupId}
                        criteriaId={rule.id}
                        name={rule.name}
                        valueKey={rule.key}
                        criteria_quantity={rule.criterial_quantity}
                    />
                )
            case RuleKeys.ddu_ddp:
                return (
                    <SwitchCriteriaDetail
                        id={rule.id}
                        name={rule.name}
                        criteria_configurations={rule.criteria_configurations}
                    />
                )
            case 'prc':
                return (
                    <div key={rule.id} className="flex items-center mb-2">
                        <h3 className="text-[#141A1F] text-[14px] font-normal">
                            PRC:
                            <span className="ml-2 bg-[#F1F5F9] text-[#3E5060] rounded px-2 py-1 text-sm">
                                {rule.criteria_configurations?.first_value === 'true' ? 'Sí' : 'No'}
                            </span>
                        </h3>
                    </div>
                )
            case RuleKeys.measurements:
                return (
                    <MeasurementCriteriaDetail
                        id={rule.id}
                        name={rule.name}
                        criteria_list={rule.criterial_list}
                    />
                )
            case RuleKeys.country:
                return (
                    <CountryCriteriaDetail
                        id={rule.id}
                        name={rule.name}
                        criterial_list={rule.criterial_list ?? []}
                    />
                )
            case RuleKeys.zone:
                return (
                    <div key={rule.id} className="flex items-center mb-2">
                        <ZonesIcon />
                        <h3 className="text-[#141A1F] text-[14px] font-normal ml-2">
                            Zonas:
                        </h3>
                        <div className="ml-2">
                            <ul className="flex gap-2 items-center">
                                {rule.criterial_list && rule.criterial_list.length > 0 ? (
                                    rule.criterial_list.map((item: any) => (
                                        <li
                                            key={item.id}
                                            className="flex items-center text-[#3E5060] text-[14px] font-normal"
                                        >
                                            <span className="bg-[#F1F5F9] rounded px-2 py-1 text-sm">
                                                {item.code || item.value || item.name}
                                            </span>
                                        </li>
                                    ))
                                ) : (
                                    <p className="text-gray-400 text-sm">
                                        No hay zonas seleccionadas
                                    </p>
                                )}
                            </ul>
                        </div>
                    </div>
                )
            default:
                return null
        }
    }

    return (
        <Accordion
            className="border-none"
            allowZeroExpanded
            onChange={(e: string[]) => setExpandedItems(e)}
        >
            {rules.map((item, index) => (
                <AccordionItem uuid={index.toString()}>
                    <AccordionItemHeading>
                        <AccordionItemButton className="py-5">
                            <div className="flex justify-between items-center">
                                <h2 className="text-lg font-bold text-slate-600">
                                    {item.name}
                                </h2>

                                <p className="text-sm text-slate-500 flex items-center">
                                    {!isClassification && item.chutes.length > 0 && (
                                        <span className="mr-2">{item.chutes.length} chutes</span>
                                    )}
                                    <span className="accordion-arrow ml-2 transition-transform duration-300 [.accordion__button[aria-expanded='false']>&]:rotate-180">
                                        {expandedItems.includes(
                                            index.toString()
                                        ) ? (
                                            <img
                                                src={ArrowUpIcon}
                                                alt="ArrowUpIcon"
                                            />
                                        ) : (
                                            <img
                                                src={ArrowIcon}
                                                alt="ArrowIcon"
                                            />
                                        )}
                                    </span>
                                </p>
                            </div>
                        </AccordionItemButton>
                    </AccordionItemHeading>
                    <AccordionItemPanel
                        className={`px-5 pb-5 ${
                            index === rules.length - 1 && 'border-none'
                        }`}
                    >
                        {!isClassification && (
                            <div className="flex items-center gap-2 mb-5 flex-wrap">
                                {item.chutes.map((chute, i) => (
                                    <h1
                                        key={i}
                                        className={`w-[32px] h-[32px] flex justify-center items-center rounded-full ${'bg-[#F1F9EC]'}`}
                                    >
                                        {chute}
                                    </h1>
                                ))}
                            </div>
                        )}
                        <p>
                            {sortCriteria(item.criterias).map((rule) => {
                                return renderCriteria(rule, item.id)
                            })}
                        </p>
                    </AccordionItemPanel>
                </AccordionItem>
            ))}
        </Accordion>
    )
}
